import { type RefObject, useLayoutEffect } from 'react';
import type { View } from 'react-native';

/** Cells tagged in DataTable via `data-export-cell` + row/col indices. */
const CELL_SELECTOR = '[data-export-cell]';

function escapeHtml(text: string) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function getCellText(element: HTMLElement) {
  const exportValue = element.dataset.exportValue;
  if (exportValue != null && exportValue !== '') {
    return exportValue;
  }

  // Custom cell UI (React elements): use visible text; collapse RN Web line breaks.
  return element.innerText.replace(/\s+/g, ' ').trim();
}

/**
 * Web: intercept Copy inside a DataTable so Excel/Sheets get rows and columns.
 *
 * Our tables are flex divs, so the default clipboard is one value per line. On
 * copy we find selected `[data-export-cell]` nodes, build a grid from their
 * row/col indices, and write TSV + HTML table to the clipboard.
 */
export function useTableCopy(containerRef: RefObject<View | null>) {
  useLayoutEffect(() => {
    const root = containerRef.current as unknown as HTMLElement | null;
    if (!root) {
      return;
    }

    const onCopy = (event: ClipboardEvent) => {
      const selection = window.getSelection();
      if (!selection?.rangeCount || selection.isCollapsed) {
        return;
      }

      // Only handle copy when the highlight starts and ends inside this table.
      if (
        !root.contains(selection.anchorNode) ||
        !root.contains(selection.focusNode)
      ) {
        return;
      }

      const selected: { row: number; col: number; text: string }[] = [];

      root.querySelectorAll(CELL_SELECTOR).forEach((cell) => {
        const element = cell as HTMLElement;
        if (!selection.containsNode(element, true)) {
          return;
        }

        const row = Number(element.dataset.row);
        const col = Number(element.dataset.col);
        if (Number.isNaN(row) || Number.isNaN(col)) {
          return;
        }

        selected.push({ row, col, text: getCellText(element) });
      });

      if (selected.length === 0) {
        return;
      }

      // Bounding box of selected cells; empty slots stay blank (partial selections).
      const minRow = Math.min(...selected.map((cell) => cell.row));
      const maxRow = Math.max(...selected.map((cell) => cell.row));
      const minCol = Math.min(...selected.map((cell) => cell.col));
      const maxCol = Math.max(...selected.map((cell) => cell.col));

      const grid: string[][] = [];
      for (let row = minRow; row <= maxRow; row += 1) {
        const line: string[] = [];
        for (let col = minCol; col <= maxCol; col += 1) {
          const match = selected.find(
            (cell) => cell.row === row && cell.col === col
          );
          line.push(match?.text ?? '');
        }
        grid.push(line);
      }

      const plain = grid.map((row) => row.join('\t')).join('\n');
      const html =
        '<table><tbody>' +
        grid
          .map(
            (row) =>
              '<tr>' +
              row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('') +
              '</tr>'
          )
          .join('') +
        '</tbody></table>';

      event.preventDefault();
      event.clipboardData?.setData('text/plain', plain);
      event.clipboardData?.setData('text/html', html);
    };

    root.addEventListener('copy', onCopy);
    return () => root.removeEventListener('copy', onCopy);
  }, [containerRef]);
}
